import React, {useMemo, useState, useEffect, useRef} from 'react';
import {Modal, View, Text, TouchableOpacity, Platform} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';

import {useTranslation} from 'react-i18next';
import {ConfirmLogoutModalStyles} from './ConfirmLogoutModal.styles';
import {LogOutModalProps} from './LoginProps';
import CloseIcon from '@shared/assets/icons/CloseIcon';
import { useScreenTheme } from '@shared/hooks/useScreenTheme';
import { Button, GlobalErrorModal, LoaderOverlay } from '@shared/components';
import { buttonSizes } from '@shared/constants/global';
import {useDispatch} from 'react-redux';
import {showLoader, hideLoader} from '@shared/store/loaderSlice';
import AlertCircleIcon from '@shared/assets/icons/AlertCircleIcon';

const ConfirmLogoutModal: React.FC<LogOutModalProps> = ({
  isVisible,
  onClose,
  onLogOut,
}) => {
  const {t} = useTranslation();
  const theme = useScreenTheme('ConfirmLogoutModal');
  const styles = useMemo(() => ConfirmLogoutModalStyles(theme), [theme]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Tracks whether the loader was dispatched so we only call hideLoader()
  // after we actually called showLoader() — prevents spuriously hiding a
  // loader that was already running on the parent screen (Android).
  const loaderDispatchedRef = React.useRef(false);
  const dispatch = useDispatch();
  // Ref to the modal heading — used to move focus into the modal when it opens
  // so that screen readers announce the dialog content immediately.
  const headingRef = useRef<any>(null);

  useEffect(() => {
    if (isVisible) {
      setIsSubmitting(false);
      // On web, move focus to the modal heading so screen readers announce
      // the dialog title immediately after the modal opens.
      if (Platform.OS === 'web') {
        // Use a short timeout to allow the Modal to finish rendering
        // before we attempt to focus the heading element.
        setTimeout(() => {
          headingRef.current?.focus?.();
        }, 50);
      }
    }
  }, [isVisible]);

  useEffect(() => {
    if (Platform.OS !== 'android') return;
    if (isSubmitting) {
      loaderDispatchedRef.current = true;
      dispatch(showLoader());
    } else if (loaderDispatchedRef.current) {
      // Only hide the loader if we were the ones who showed it.
      loaderDispatchedRef.current = false;
      dispatch(hideLoader());
    }
  }, [isSubmitting, dispatch]);

  return (
    <Modal
      visible={isVisible}
      transparent
      animationType="none"
      supportedOrientations={[
        'portrait',
        'landscape',
        'landscape-left',
        'landscape-right',
      ]}>
      <View style={styles.container}>
        <SafeAreaView
          style={styles.modal}
          // @ts-ignore — role/aria-modal are web-only ARIA props
          role={Platform.OS === 'web' ? 'dialog' : undefined}
          aria-modal={Platform.OS === 'web' ? true : undefined}
          aria-label={Platform.OS === 'web' ? t('common.logout.title') : undefined}>
          <View style={styles.header}>
            {/* @ts-ignore — focusable & tabIndex are web-only props not in RN Text types */}
            <View style={styles.titleMainContainer}>
              <View style={styles.iconContainer}><AlertCircleIcon /></View>
              <Text
                ref={headingRef}
                style={styles.headerTitle}
                accessible={true}
                accessibilityRole="header"
                accessibilityLabel={t('common.logout.title')}
                testID="modalHeader"
                {...(Platform.OS === 'web' ? {focusable: true, tabIndex: -1} : {})}>
                {t('common.logout.title')}
              </Text>
            </View>
            <TouchableOpacity
              onPress={() => {
                onClose();
              }}
              // @ts-ignore — onKeyDown is a valid web event not in RN types
              onKeyDown={(event: any) => {
                if (Platform.OS === 'web' && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  onClose();
                }
              }}
              testID="closeButton"
              accessible={true}
              accessibilityRole="button"
              accessibilityLabel={t('common.accessibility.modalClose')}
              accessibilityHint={t('common.accessibility.closeNoteModal')}>
              <CloseIcon color={theme.closeModalIconColor} />
            </TouchableOpacity>
          </View>

          <View style={styles.titleContainer}>
            <View style={styles.titleSubContainer}>
              {/* @ts-ignore — focusable & tabIndex are web-only props not in RN Text types */}
              <Text
                testID="logoutConfirm-modal-message"
                style={styles.message}
                accessible={true}
                accessibilityRole="text"
                {...(Platform.OS === 'web' ? {focusable: true, tabIndex: 0} : {})}>
                {t('common.logout.desc')}
              </Text>
            </View>
          </View>
          <View style={styles.buttonContainer}>
            <Button
              testID={`confirm-logout-modal-cta`}
              title={t('common.logout.cta')}
              onPress={() => {
                if (isSubmitting) return;
                setIsSubmitting(true);
                onLogOut();
              }}
              disabled={isSubmitting}
              fullWidth={false}
              size={buttonSizes.medium}
              customTextStyle={styles.customTextStyle}
              accessible={true}
              accessibilityLabel={t('common.accessibility.logoutButton')}
              accessibilityRole="button"
              accessibilityHint={t('common.accessibility.logoutHint')}
            />
          </View>
          {Platform.OS !== 'android' && <LoaderOverlay visible={isSubmitting} insideModal />}
        </SafeAreaView>
        {Platform.OS !== 'android' && <GlobalErrorModal />}
      </View>
    </Modal>
  );
};

export default ConfirmLogoutModal;
